/**
 * Fetches product recommendations and swaps them in.
 *
 * Shopify's recommendation engine is not available at first render, so the
 * section emits a placeholder carrying the endpoint and this replaces it once
 * the real markup arrives.
 *
 * Deferred until the placeholder is near the viewport — the rail sits at the
 * bottom of long PDPs, and a request on load competes with imagery that is
 * actually on screen. Falls back to fetching immediately where
 * IntersectionObserver is unavailable.
 */

async function loadInto(placeholder) {
  const url = placeholder.dataset.recommendationsUrl;
  if (!url) return;

  try {
    const response = await fetch(url);
    if (!response.ok) return;

    const markup = await response.text();
    const section = new DOMParser().parseFromString(markup, 'text/html').querySelector('.tecton-recs');

    // No recommendations for this product is a normal outcome, not an error —
    // remove the placeholder rather than leaving an empty gap in the page.
    if (section) placeholder.replaceWith(section);
    else placeholder.remove();
  } catch {
    placeholder.remove();
  }
}

function init() {
  for (const placeholder of document.querySelectorAll('.tecton-recs__placeholder')) {
    if (placeholder.dataset.loading === '') continue;
    placeholder.dataset.loading = '';

    if (!('IntersectionObserver' in window)) {
      loadInto(placeholder);
      continue;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        loadInto(placeholder);
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(placeholder);
  }
}

init();

// The theme editor re-renders sections in place; re-scan when it does.
document.addEventListener('shopify:section:load', init);
